import axios from 'axios';
import { refreshAccessToken, getAccessToken, clearTokens } from 'src/helpers/auth';
import { notify } from 'src/helpers/notify';
import { reportApiError } from 'src/helpers/apiError';
import { websocketEvents } from './websocket';

declare module 'axios' {
  export interface AxiosRequestConfig {
    silent?: boolean;
  }
}

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
/*LocalStorage.set(
  "JWT",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOjEsImVtYWlsIjoiamhAaXRtYXN0ZXIuZGsiLCJwZXJtcyI6WyJiYXRjaGVzLmNyZWF0ZSIsImJhdGNoZXMucmVhZCIsImJhdGNoZXMudXBkYXRlIiwiYmF0Y2hlcy5kZWxldGUiLCJjdXN0b21lcnMuY3JlYXRlIiwiY3VzdG9tZXJzLnJlYWQiLCJjdXN0b21lcnMudXBkYXRlIiwiY3VzdG9tZXJzLmRlbGV0ZSIsInVzZXJzLmNyZWF0ZSIsInVzZXJzLnJlYWQiLCJ1c2Vycy51cGRhdGUiLCJ1c2Vycy5kZWxldGUiLCJvcmRlcnMuY3JlYXRlIiwib3JkZXJzLnJlYWQiLCJvcmRlcnMudXBkYXRlIiwib3JkZXJzLmRlbGV0ZSIsInByb2R1Y3RzLmNyZWF0ZSIsInByb2R1Y3RzLnJlYWQiLCJwcm9kdWN0cy51cGRhdGUiLCJwcm9kdWN0cy5kZWxldGUiLCJzdG9ja3MuY3JlYXRlIiwic3RvY2tzLnJlYWQiLCJzdG9ja3MudXBkYXRlIiwic3RvY2tzLmRlbGV0ZSIsInN1cHBsaWVycy5jcmVhdGUiLCJzdXBwbGllcnMucmVhZCIsInN1cHBsaWVycy51cGRhdGUiLCJzdXBwbGllcnMuZGVsZXRlIiwid2FyZWhvdXNlcy5jcmVhdGUiLCJ3YXJlaG91c2VzLnJlYWQiLCJ3YXJlaG91c2VzLnVwZGF0ZSIsIndhcmVob3VzZXMuZGVsZXRlIiwicHVyY2hhc2Vfb3JkZXJzLmNyZWF0ZSIsInB1cmNoYXNlX29yZGVycy5yZWFkIiwicHVyY2hhc2Vfb3JkZXJzLnVwZGF0ZSIsInB1cmNoYXNlX29yZGVycy5kZWxldGUiXSwiaWF0IjoxNjM2NzQ1NTAxLCJleHAiOjE2MzY4MzE5MDF9.AsZnSeUcWUW4EOqIDrFsphRsefrW3y_p04it0bWSrGE"
);*/

// No import-time localStorage read: the request interceptor below sets the
// authorization header on every request from the current token.
const api = axios.create({
  baseURL: process.env.API,
});

// Request interceptor to always use the latest token
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't try to refresh if this is already a refresh request or logout request
    const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');
    const isLogoutRequest = originalRequest.url?.includes('/auth/logout');
    const isLoginRequest = originalRequest.url?.includes('/auth/login');

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest && !isLogoutRequest && !isLoginRequest) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.authorization = `Bearer ${newToken}`;
        
        // Notify websocket about token refresh
        websocketEvents.dispatchEvent(new CustomEvent('http-token-refreshed', { 
          detail: { token: newToken } 
        }));
        
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        console.log('Token refresh failed, redirecting to login');
        clearTokens();
        
        // Use setTimeout to avoid potential issues with immediate redirect
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
        
        return Promise.reject(refreshError);
      }
    }

    // For logout requests that fail with 401, just ignore the error
    if (error.response?.status === 401 && isLogoutRequest) {
      console.log('Logout request failed with 401, but continuing logout process');
      return Promise.resolve({ data: { message: 'Logout completed' } });
    }

    // Log with a correlation code and toast (deduped for GET bursts) — see
    // helpers/apiError.ts; the rejection itself is passed through untouched.
    reportApiError(error, { notify });

    return Promise.reject(error);
  }
);

// Export for manual setup in main.ts
export const setupAxios = (app) => {
  // for use inside Vue files (Options API) through this.$axios and this.$api

  app.config.globalProperties.$axios = axios;
  // ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
  //       so you won't necessarily have to import axios in each vue file

  app.config.globalProperties.$api = api;
  // ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
  //       so you can easily perform requests against your app's API
};

export { api };
